import * as HLF from "html-living-framework";
import * as springroll from "springroll";
import {AudioChannel} from "./Core/AudioChannel";
import {Assets, updateStateAssets} from "./Data/Assets";
import {CaptionManager} from "./Managers/CaptionManager";
import {StartScreen} from "./States/StartScreen";
import {LoadScreen} from "./States/LoadScreen";
import {DefaultCaptionRenderer} from "./GameObjects/DefaultCaptionRenderer";
import {Environment} from "./Data/Environment";
import {StateName} from "./Data/StateName";
import {HomeScreen} from "./States/HomeScreen";
import {KitchenScreen} from "./States/KitchenScreen";
import {PuzzleScreen} from "./States/PuzzleScreen";
import {IntroScreen} from "./States/IntroScreen";
import {MovieScreen} from "./States/MovieScreen";
import {MonsterScreen} from "./States/MonsterScreen";
import {RobotScreen} from "./States/RobotScreen";
import {GoogleAnalytics} from "springroll-google-analytics-plugin";
import {Analytics} from "./Managers/Analytics";

export class Application extends HLF.Application {

    private _springroll!: springroll.Application;
    private _paused = false;

    constructor() {
        super({
            transparent: true,
            preserveDrawingBuffer: (
                PIXI.utils.isMobile.android.device ||
                PIXI.utils.isMobile.amazon.device
            ) ? true : undefined,
        });

        this._audioManager.debug = true;
        this._stateManager.debug = true;
        this._loadManager.debug = true;
        this._popupManager.debug = true;

        PIXI.settings.FILTER_RESOLUTION = this.renderer.resolution;

        // Fixing 404 of webm audio
        (HLF.HowlerTrack as any).getDefaultUrls = (id: string) => {
            return [`${HLF.AssetUtils.FILEPATH_AUDIO}${id}.mp3`];
        };
    }

    static get instance() {
        if (HLF.Application._instance === undefined) {
            HLF.Application._instance = new Application();
        }
        return HLF.Application._instance as Application;
    }

    get springroll(): springroll.Application {
        return this._springroll;
    }

    public get requiredAssets(): HLF.AssetMapData[] {
        return [];
    }

    public createAssetMap() {
        super.createAssetMap();

        HLF.AssetMap.addAssetGroup(HLF.SplashScreen.NAME, Assets.boot);
        HLF.AssetMap.addAssetGroup(StateName.Start, Assets.common);
        HLF.AssetMap.addAssetGroup(StateName.Intro, [
            ...Assets.common,
            ...Assets.intro,
        ]);
    }

    public createSplashScreen() {
        return super.createSplashScreen();
    }

    public registerStates() {
        super.registerStates();

        this._stateManager.registerState(StateName.Intro, () => new IntroScreen());
        this._stateManager.registerState(StateName.Home, () => new HomeScreen());
        this._stateManager.registerState(StateName.Kitchen, () => new KitchenScreen());
        this._stateManager.registerState(StateName.Robot, () => new RobotScreen());
        this._stateManager.registerState(StateName.Puzzle, () => new PuzzleScreen());
        this._stateManager.registerState(StateName.Movie, () => new MovieScreen());
        this._stateManager.registerState(StateName.Monster, () => new MonsterScreen());
    }

    public registerLoadingScreens() {
        super.registerLoadingScreens();

        this._loadManager.registerLoadScreen(StateName.Start, () => new StartScreen());
        this._loadManager.registerLoadScreen(StateName.Load, () => new LoadScreen());
    }

    public registerPopups() {
        super.registerPopups();
    }

    public update() {
        super.update();
    }

    public init() {
        super.init();

        this.initSpringroll();

        this.resizer.sizeMax = new PIXI.Point(1664, 768);

        this.initExitGameHandler();

        this.renderer.view.addEventListener("contextmenu", e => {
            e.preventDefault();
        });
    }

    protected onResizeComplete() {
        CaptionManager.instance.onResize(this.resizer.getSize());
    }

    protected onLoadRequiredAssetsComplete() {
        super.onLoadRequiredAssetsComplete();

        const captionRenderer = this.stage.addChild(new DefaultCaptionRenderer());
        CaptionManager.instance.setRenderer(captionRenderer);
        CaptionManager.instance.onResize(this._size);

        const data = {stateName: StateName.Intro, itemData: undefined};
        if (data.stateName !== StateName.Intro) {
            updateStateAssets(data.stateName, data.itemData);
        }
        PubSub.publishSync(
            HLF.STATE_LOAD_STATE,
            new HLF.StateToken({id: data.stateName, data: data.itemData},
                StateName.Start,
                HLF.TransitionStep.ShowLoadScreen,
                ...HLF.StateToken.TRANSITION_FIRST_VIEW),
        );
        PubSub.publish(HLF.PUSH_KEYBOARD_LAYER, undefined);
    }

    private initSpringroll(): void {
        this._springroll = new springroll.Application({
            features: {
                captions: true,
                sound: true,
                vo: true,
                music: true,
                sfx: true,
            },
        });
        springroll.Application.uses(new GoogleAnalytics());

        this._springroll.state.ready.subscribe(() => {
            this.onSpringrollInitialized();
        });

        this._springroll.state.pause.subscribe((isPaused: boolean) => {
            this.onPause(isPaused);
        });

        this._springroll.state.playOptions.subscribe((playOptions: any) => {
            console.log("New playOptions value set to", playOptions);
        });

        this._springroll.state.captionsMuted.subscribe((result: boolean) => {
            this.onCaptionsMuted(result);
        });

        this._springroll.state.soundVolume.subscribe((result: number) => {
            this.onSoundVolume(result);
        });

        this._springroll.state.musicVolume.subscribe((result: number) => {
            this.onMusicVolume(result * 0.85);
        });

        this._springroll.state.voVolume.subscribe((result: number) => {
            this.onVOVolume(result);
        });

        this._springroll.state.sfxVolume.subscribe((result: number) => {
            this.onSFXVolume(result);
        });
    }

    private onSpringrollInitialized(): void {
        // override
    }

    // Springroll message listeners
    private onCaptionsMuted(pMuted: boolean): void {
        if (Environment.isLive()) {
            CaptionManager.instance.active = !pMuted;
        }
    }

    private onSoundVolume(pResults: number): void {
        AudioChannel.mute = pResults === 0;
        this.audio.masterVolume = pResults;
    }

    private onVOVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.VO, pResults);
    }

    private onMusicVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.BGM, pResults * 0.4);
    }

    private onSFXVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.SFX, pResults);
    }

    private onPause(pIsPaused: boolean): void {
        const wasPaused = this._paused;
        if (pIsPaused && !wasPaused) {
            AudioChannel.mute = true;
            PubSub.publishSync(HLF.GAME_PAUSED, undefined);
        } else if (wasPaused && !pIsPaused) {
            AudioChannel.mute = false;
            PubSub.publishSync(HLF.GAME_UNPAUSED, undefined);
        }
        this._paused = pIsPaused;
    }

    private initExitGameHandler() {
        window.addEventListener("beforeunload", (event) => {
            Analytics.event("endGame");
        });
    }
}
